import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = [
    'loading',
    // Executive targets
    'dau', 'dauTrend', 'dauSparkline', 'mau', 'wau', 'sessions', 'sessionsSparkline',
    'errorRate', 'avgSession', 'funnelSignup', 'funnelSync', 'funnelGroup',
    // Engagement targets
    'dauChart', 'wauMauChart', 'sessionsChart', 'durationChart',
    'topScreensChart', 'stickinessChart', 'screensPerSessionChart',
    // Health targets
    'syncRate', 'syncSuccess', 'syncFailed', 'permissionRate',
    'workouts', 'workoutUsers', 'nutritionRate',
    'syncTrendChart', 'syncByTypeChart', 'wearablesChart', 'syncErrors',
    // Social targets
    'netGroups', 'groupsCreated', 'groupsDeleted', 'groupJoins',
    'inviteConversion', 'invitesSent', 'messages', 'messageSenders',
    'joinSourcesChart', 'inviteMethodsChart', 'dailyActivityChart',
    'leaderboardViews', 'challengesJoined', 'challengesCompleted', 'challengeCompletionRate',
    'topGroups',
    // Drilldown modal targets
    'drilldownModal', 'drilldownTitle', 'drilldownContent', 'drilldownLoading',
    'drilldownBreadcrumbRoot', 'drilldownBreadcrumbTrail',
    'drilldownPagination', 'drilldownResultCount', 'drilldownCurrentPage', 'drilldownTotalPages',
    'drilldownPrevBtn', 'drilldownNextBtn', 'drilldownExportLink',
    // Toast container
    'toastContainer',
    // Connection status
    'connectionStatus', 'connectionIndicator'
  ]

  static values = {
    url: String,
    startDate: String,
    endDate: String,
    dateRange: Number,
    drilldownUrl: String,
    drilldownExportUrl: String,
    websocketEnabled: { type: Boolean, default: false },
    dashboardType: { type: String, default: 'executive' }
  }

  connect() {
    this.charts = {}
    this.isLoading = false
    this.drilldownState = {
      type: null,
      params: {},
      breadcrumbs: [],
      page: 1,
      totalPages: 1,
      totalResults: 0
    }

    // Connection monitoring state
    this.connectionState = {
      status: 'disconnected',
      connectionId: null,
      connectedAt: null,
      lastPong: null,
      latencyMs: null,
      reconnectAttempts: 0
    }

    this.loadData()

    // Initialize WebSocket if enabled
    if (this.websocketEnabledValue) {
      this.initializeWebSocket()
    }
  }

  disconnect() {
    this.destroyAllCharts()

    // Clean up WebSocket
    this.disconnectWebSocket()

    // Stop heartbeat timer
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }

    // Clean up event listeners
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler)
      this.escapeHandler = null
    }
  }

  destroyAllCharts() {
    Object.keys(this.charts).forEach((name) => {
      if (this.charts[name]) {
        this.charts[name].destroy()
        this.charts[name] = null
      }
    })
    this.charts = {}
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate
    this.loadData()
  }

  async loadData() {
    if (this.isLoading) return
    this.isLoading = true
    this.showLoading()

    try {
      const params = new URLSearchParams()
      if (this.hasDateRangeValue && this.dateRangeValue) {
        params.set('days', this.dateRangeValue)
      }
      if (this.hasStartDateValue && this.startDateValue) {
        params.set('start_date', this.startDateValue)
      }
      if (this.hasEndDateValue && this.endDateValue) {
        params.set('end_date', this.endDateValue)
      }

      const response = await fetch(`${this.urlValue}?${params}`)
      const data = await response.json()

      this.destroyAllCharts()
      this.updateDashboard(data)
    } catch (error) {
      console.error('Failed to load analytics data:', error)
      this.showToast('Failed to load analytics data. Please try again.', 'error')
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  updateDashboard(data) {
    // Detect dashboard type and update accordingly
    if (data.funnel_summary !== undefined) {
      this.updateExecutiveDashboard(data)
    } else if (data.dau_trend !== undefined && data.feature_stickiness !== undefined) {
      this.updateEngagementDashboard(data)
    } else if (data.sync_success_rate !== undefined) {
      this.updateHealthDashboard(data)
    } else if (data.group_creation !== undefined) {
      this.updateSocialDashboard(data)
    }
  }

  updateExecutiveDashboard(data) {
    this.setTextContent('dau', this.formatNumber(data.dau || 0))
    this.setTextContent('mau', this.formatNumber(data.mau || 0))
    this.setTextContent('wau', this.formatNumber(data.wau || 0))
    this.setTextContent('sessions', this.formatNumber(data.sessions_today || 0))
    this.setTextContent('avgSession', data.avg_session_duration || 0)
    this.setTextContent('errorRate', data.error_rate || 0)

    // DAU trend
    if (this.hasDauTrendTarget) {
      const trend = data.dau_trend || 0
      this.dauTrendTarget.textContent = `${trend >= 0 ? '+' : ''}${trend}%`
      this.dauTrendTarget.classList.toggle('bg-green-100', trend >= 0)
      this.dauTrendTarget.classList.toggle('text-green-800', trend >= 0)
      this.dauTrendTarget.classList.toggle('bg-red-100', trend < 0)
      this.dauTrendTarget.classList.toggle('text-red-800', trend < 0)
    }

    // Funnel
    if (data.funnel_summary) {
      this.setTextContent('funnelSignup', this.formatNumber(data.funnel_summary.signup || 0))
      this.setTextContent('funnelSync', this.formatNumber(data.funnel_summary.first_sync || 0))
      this.setTextContent('funnelGroup', this.formatNumber(data.funnel_summary.first_group || 0))
    }

    // Sparklines
    if (data.sparklines) {
      this.renderSparkline('dauSparkline', data.sparklines.dau)
      this.renderSparkline('sessionsSparkline', data.sparklines.sessions)
    }
  }

  updateEngagementDashboard(data) {
    if (data.dau_trend) {
      this.renderLineChart('dauChart', data.dau_trend, 'DAU', {
        type: 'time',
        label: 'DAU_BY_DATE',
        params: { granularity: 'day' }
      })
    }
    if (data.wau_trend && data.mau_trend) {
      this.renderMultiLineChart('wauMauChart', [
        { label: 'WAU', data: data.wau_trend },
        { label: 'MAU', data: data.mau_trend }
      ])
    }
    if (data.sessions_per_day) {
      this.renderLineChart('sessionsChart', data.sessions_per_day, 'Sessions', {
        type: 'time',
        label: 'SESSIONS_BY_DATE',
        params: { granularity: 'day', event_category: 'session' }
      })
    }
    if (data.avg_session_duration_trend) {
      this.renderLineChart('durationChart', data.avg_session_duration_trend, 'Duration (s)', {
        type: 'time',
        label: 'DURATION_BY_DATE',
        params: { granularity: 'day' }
      })
    }
    if (data.top_screens) {
      this.renderBarChart('topScreensChart', data.top_screens, 'Views', {
        type: 'screen',
        label: 'SCREEN_DETAIL',
        params: {}
      })
    }
    if (data.feature_stickiness) {
      this.renderLineChart('stickinessChart', data.feature_stickiness, 'Stickiness %', {
        type: 'time',
        label: 'STICKINESS_BY_DATE',
        params: { granularity: 'day' }
      })
    }
    if (data.screens_per_session) {
      this.renderBarChart(
        'screensPerSessionChart',
        Object.fromEntries(data.screens_per_session.labels.map((l, i) => [l, data.screens_per_session.data[i]])),
        'Sessions',
        { type: 'feature', label: 'FEATURE_DETAIL', params: {} }
      )
    }
  }

  updateHealthDashboard(data) {
    if (data.sync_success_rate) {
      this.setTextContent('syncRate', data.sync_success_rate.rate || 0)
      this.setTextContent('syncSuccess', this.formatNumber(data.sync_success_rate.success || 0))
      this.setTextContent('syncFailed', this.formatNumber(data.sync_success_rate.failed || 0))
    }
    if (data.permission_grants) {
      this.setTextContent('permissionRate', data.permission_grants.grant_rate || 0)
    }
    if (data.workout_frequency) {
      this.setTextContent('workouts', this.formatNumber(data.workout_frequency.total_workouts || 0))
      this.setTextContent('workoutUsers', this.formatNumber(data.workout_frequency.unique_users || 0))
    }
    if (data.nutrition_adoption) {
      this.setTextContent('nutritionRate', data.nutrition_adoption.adoption_rate || 0)
    }
    if (data.daily_syncs) {
      this.renderStackedBarChart('syncTrendChart', data.daily_syncs)
    }
    if (data.sync_by_type) {
      this.renderBarChart('syncByTypeChart', data.sync_by_type, 'Syncs', {
        type: 'sync',
        label: 'SYNC_DETAIL',
        params: {}
      })
    }
    if (data.wearable_connections) {
      const wearableData = {}
      Object.entries(data.wearable_connections).forEach(([k, v]) => {
        wearableData[k] = v.users || 0
      })
      this.renderBarChart('wearablesChart', wearableData, 'Users', {
        type: 'device',
        label: 'DEVICE_DETAIL',
        params: {}
      })
    }
    if (data.sync_errors && this.hasSyncErrorsTarget) {
      this.syncErrorsTarget.innerHTML = Object.entries(data.sync_errors)
        .map(([error, count]) => `
          <div class="flex justify-between items-center py-2 border-b border-slate-200 cursor-pointer hover:bg-slate-50"
               data-action="click->admin--analytics-dashboard#drilldownError"
               data-error-type="${this.escapeHtml(error)}">
            <span class="text-xs text-slate-600 truncate mr-2">${this.escapeHtml(error)}</span>
            <span class="text-sm font-bold text-slate-900">${count}</span>
          </div>
        `).join('')
    }
  }

  updateSocialDashboard(data) {
    if (data.group_creation) {
      this.setTextContent('netGroups', this.formatNumber(data.group_creation.net_new || 0))
      this.setTextContent('groupsCreated', this.formatNumber(data.group_creation.total_created || 0))
      this.setTextContent('groupsDeleted', this.formatNumber(data.group_creation.total_deleted || 0))
    }
    if (data.group_joins) {
      this.setTextContent('groupJoins', this.formatNumber(data.group_joins.total || 0))
    }
    if (data.invitations) {
      this.setTextContent('inviteConversion', data.invitations.conversion_rate || 0)
      this.setTextContent('invitesSent', this.formatNumber(data.invitations.sent || 0))
    }
    if (data.messaging_activity) {
      this.setTextContent('messages', this.formatNumber(data.messaging_activity.total_messages || 0))
      this.setTextContent('messageSenders', this.formatNumber(data.messaging_activity.unique_senders || 0))
    }
    if (data.group_joins?.breakdown_percentages) {
      this.renderPieChart('joinSourcesChart', {
        'Invite': data.group_joins.via_invite,
        'Search': data.group_joins.via_search,
        'Link': data.group_joins.via_link,
        'Other': data.group_joins.via_other
      })
    }
    if (data.invite_by_method) {
      this.renderPieChart('inviteMethodsChart', {
        'Link': data.invite_by_method.link,
        'Email': data.invite_by_method.email,
        'In-App': data.invite_by_method.in_app,
        'SMS': data.invite_by_method.sms
      })
    }
    if (data.daily_social_activity) {
      this.renderMultiLineChart('dailyActivityChart', [
        { label: 'Group Joins', data: { labels: data.daily_social_activity.labels, data: data.daily_social_activity.group_joins } },
        { label: 'Invites', data: { labels: data.daily_social_activity.labels, data: data.daily_social_activity.invites_sent } },
        { label: 'Messages', data: { labels: data.daily_social_activity.labels, data: data.daily_social_activity.messages_sent } }
      ])
    }
    if (data.leaderboard_engagement) {
      this.setTextContent('leaderboardViews', this.formatNumber(data.leaderboard_engagement.total_views || 0))
      this.setTextContent('challengesJoined', this.formatNumber(data.leaderboard_engagement.challenges_joined || 0))
      this.setTextContent('challengesCompleted', this.formatNumber(data.leaderboard_engagement.challenges_completed || 0))
      this.setTextContent('challengeCompletionRate', data.leaderboard_engagement.challenge_completion_rate || 0)
    }
    if (data.top_groups && this.hasTopGroupsTarget) {
      this.topGroupsTarget.innerHTML = (data.top_groups || [])
        .map((group, i) => `
          <div class="flex justify-between items-center py-2 border-b border-slate-200">
            <span class="text-xs text-slate-600">#${i + 1} ${this.escapeHtml(String(group.group_id || ''))}</span>
            <span class="text-sm font-bold text-slate-900">${this.formatNumber(group.activity_count || 0)} EVENTS</span>
          </div>
        `).join('') || '<p class="text-xs text-slate-500">NO_GROUP_DATA</p>'
    }
  }

  // Chart rendering methods
  renderSparkline(targetName, data) {
    const target = this[`${targetName}Target`]
    if (!target || !data?.length) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    this.charts[targetName] = new Chart(target, {
      type: 'line',
      data: {
        labels: data.map((_, i) => i),
        datasets: [{
          data: data,
          borderColor: '#171717',
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { display: false },
          y: { display: false }
        }
      }
    })
  }

  renderLineChart(targetName, chartData, label, drilldownConfig = null) {
    const target = this[`${targetName}Target`]
    if (!target) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 7 } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } }
      }
    }

    // Add onClick handler for drilldown if configured
    if (drilldownConfig && this.hasDrilldownUrlValue) {
      chartOptions.onClick = (_evt, elements) => {
        if (!elements.length) return
        const element = elements[0]
        const clickedLabel = chartData?.labels?.[element.index]
        if (!clickedLabel) return

        const params = { ...drilldownConfig.params }
        if (drilldownConfig.type === 'time') {
          params.date = clickedLabel
          params.granularity = 'hour'
        }
        this.openDrilldown(drilldownConfig.type, params, drilldownConfig.label || clickedLabel)
      }
    }

    this.charts[targetName] = new Chart(target, {
      type: 'line',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          label: label,
          data: chartData?.data || [],
          fill: true,
          backgroundColor: 'rgba(23, 23, 23, 0.1)',
          borderColor: '#171717',
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4
        }]
      },
      options: chartOptions
    })
  }

  renderMultiLineChart(targetName, datasets) {
    const target = this[`${targetName}Target`]
    if (!target) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    const colors = ['#171717', '#525252', '#a3a3a3']

    this.charts[targetName] = new Chart(target, {
      type: 'line',
      data: {
        labels: datasets[0]?.data?.labels || [],
        datasets: datasets.map((ds, i) => ({
          label: ds.label,
          data: ds.data?.data || [],
          borderColor: colors[i % colors.length],
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 7 } },
          y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } }
        }
      }
    })
  }

  renderBarChart(targetName, chartData, label, drilldownConfig = null) {
    const target = this[`${targetName}Target`]
    if (!target) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    const labels = Object.keys(chartData || {}).slice(0, 10)
    const values = Object.values(chartData || {}).slice(0, 10)

    const chartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
        y: { grid: { display: false } }
      }
    }

    // Add onClick handler for drilldown if configured
    if (drilldownConfig && this.hasDrilldownUrlValue) {
      chartOptions.onClick = (_evt, elements) => {
        if (!elements.length) return
        const element = elements[0]
        const clickedLabel = labels[element.index]
        if (!clickedLabel) return

        const params = { ...drilldownConfig.params }
        if (drilldownConfig.type === 'screen') {
          params.screen_name = clickedLabel
        } else if (drilldownConfig.type === 'feature') {
          params.feature_name = clickedLabel
        } else if (drilldownConfig.type === 'error') {
          params.error_type = clickedLabel
        } else if (drilldownConfig.type === 'sync') {
          params.sync_type = clickedLabel
        } else if (drilldownConfig.type === 'device') {
          params.device_type = clickedLabel
        }
        this.openDrilldown(drilldownConfig.type, params, drilldownConfig.label || clickedLabel)
      }
    }

    this.charts[targetName] = new Chart(target, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: label,
          data: values,
          backgroundColor: 'rgba(23, 23, 23, 0.7)',
          borderColor: '#171717',
          borderWidth: 1
        }]
      },
      options: chartOptions
    })
  }

  renderStackedBarChart(targetName, chartData) {
    const target = this[`${targetName}Target`]
    if (!target) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    this.charts[targetName] = new Chart(target, {
      type: 'bar',
      data: {
        labels: chartData?.labels || [],
        datasets: [
          {
            label: 'Success',
            data: chartData?.success || [],
            backgroundColor: 'rgba(34, 197, 94, 0.7)',
            borderColor: 'rgb(34, 197, 94)',
            borderWidth: 1
          },
          {
            label: 'Failed',
            data: chartData?.failed || [],
            backgroundColor: 'rgba(239, 68, 68, 0.7)',
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: { stacked: true, beginAtZero: true }
        }
      }
    })
  }

  renderPieChart(targetName, chartData) {
    const target = this[`${targetName}Target`]
    if (!target) return

    if (this.charts[targetName]) {
      this.charts[targetName].destroy()
    }

    const labels = Object.keys(chartData || {})
    const values = Object.values(chartData || {})

    this.charts[targetName] = new Chart(target, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: ['#171717', '#525252', '#a3a3a3', '#d4d4d4'],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'right' } }
      }
    })
  }

  // Helpers
  setTextContent(targetName, value) {
    const targetExists = this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]
    if (targetExists) {
      this[`${targetName}Target`].textContent = value
    }
  }

  formatNumber(num) {
    return new Intl.NumberFormat().format(num)
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  // Safe wrapper for escaping values with fallback
  safe(value, fallback = '-') {
    return this.escapeHtml(value ?? fallback)
  }

  // Click handler for error drilldown from sync errors list
  drilldownError(event) {
    const errorType = event.currentTarget.dataset.errorType
    if (!errorType) return

    this.openDrilldown('error', { error_type: errorType }, `ERROR: ${errorType}`)
  }

  // Click handler for user drilldown from stat cards
  drilldownUsers(event) {
    const el = event.currentTarget
    const params = {}

    // Support stat card clicks with metric-type
    const metricType = el.dataset.metricType
    const metricLabel = el.dataset.metricLabel || 'USERS'

    if (metricType) {
      params.metric_type = metricType
    }

    // Support event-based drilldowns
    const eventName = el.dataset.eventName
    const eventCategory = el.dataset.eventCategory
    if (eventName) params.event_name = eventName
    if (eventCategory) params.event_category = eventCategory

    this.openDrilldown('users', params, metricLabel)
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  // Drilldown methods
  async openDrilldown(type, params = {}, label = null) {
    if (!this.hasDrilldownModalTarget || !this.hasDrilldownUrlValue) return

    // Reset state for new drilldown
    this.drilldownState = {
      type: type,
      params: params,
      breadcrumbs: [{ label: label || type.toUpperCase(), type: type, params: params }],
      page: 1,
      totalPages: 1,
      totalResults: 0
    }

    this.showDrilldownModal()
    await this.loadDrilldownData()
  }

  async drillInto(type, params = {}, label = null) {
    // Add to breadcrumb trail
    this.drilldownState.breadcrumbs.push({
      label: label || type.toUpperCase(),
      type: type,
      params: params
    })
    this.drilldownState.type = type
    this.drilldownState.params = params
    this.drilldownState.page = 1

    await this.loadDrilldownData()
  }

  navigateDrilldown(event) {
    event.preventDefault()
    const index = event.currentTarget.dataset.breadcrumbIndex

    if (index === undefined) {
      // Clicked root - close drilldown
      this.closeDrilldown()
      return
    }

    // Truncate breadcrumbs to selected level
    const targetIndex = parseInt(index, 10)
    const breadcrumb = this.drilldownState.breadcrumbs[targetIndex]
    if (!breadcrumb) return

    this.drilldownState.breadcrumbs = this.drilldownState.breadcrumbs.slice(0, targetIndex + 1)
    this.drilldownState.type = breadcrumb.type
    this.drilldownState.params = breadcrumb.params
    this.drilldownState.page = 1

    this.loadDrilldownData()
  }

  async loadDrilldownData() {
    this.showDrilldownLoading()

    try {
      const params = new URLSearchParams({
        type: this.drilldownState.type,
        page: this.drilldownState.page,
        ...this.drilldownState.params
      })

      if (this.hasStartDateValue) params.set('start_date', this.startDateValue)
      if (this.hasEndDateValue) params.set('end_date', this.endDateValue)

      const response = await fetch(`${this.drilldownUrlValue}?${params}`)

      if (response.status === 429) {
        this.renderDrilldownError('Rate limit exceeded. Please try again later.')
        return
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      // Read pagination from nested pagination object or top-level (for backwards compatibility)
      this.drilldownState.totalResults = data.pagination?.total_count || data.total_count || data.data?.length || 0
      this.drilldownState.totalPages = data.pagination?.total_pages || data.total_pages || 1

      this.renderDrilldownData(data)
      this.updateDrilldownPagination()
      this.updateDrilldownBreadcrumbs()
      this.updateDrilldownExportLink()
    } catch (error) {
      console.error('Drilldown load failed:', error)
      this.renderDrilldownError('Failed to load data. Please try again.')
    } finally {
      this.hideDrilldownLoading()
    }
  }

  renderDrilldownData(data) {
    if (!this.hasDrilldownContentTarget) return

    const type = this.drilldownState.type
    let html = ''

    // Extract data based on drilldown type's response shape
    switch (type) {
      case 'users': {
        const users = data.users || []
        if (!users.length) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderUsersTable(users)
        }
        break
      }
      case 'time': {
        // Time data comes as a hash {hour: count}, convert to array
        const timeData = data.data || {}
        const items = Object.entries(timeData).map(([time, count]) => ({
          time_bucket: time,
          event_count: count
        }))
        if (!items.length) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderTimeTable(items)
        }
        break
      }
      case 'screen': {
        // Screen returns single object with metrics, render as summary
        if (!data.screen) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderScreenSummary(data)
        }
        break
      }
      case 'error': {
        const errors = data.errors || []
        if (!errors.length) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderErrorTable(errors)
        }
        break
      }
      case 'group': {
        // Groups can be array (ranked) or single object
        const groups = data.groups || (data.group_id ? [data] : [])
        if (!groups.length) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderGroupTable(groups)
        }
        break
      }
      case 'device': {
        // Device returns hash breakdown, render as summary tables
        if (!data.device_type && !data.total_events) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderDeviceSummary(data)
        }
        break
      }
      case 'sync': {
        // Sync returns hash with by_status and error_breakdown
        if (!data.sync_type) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderSyncSummary(data)
        }
        break
      }
      case 'workout': {
        // Workout returns hash with by_type breakdown
        if (!data.total_workouts && data.total_workouts !== 0) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderWorkoutSummary(data)
        }
        break
      }
      case 'nutrition': {
        // Nutrition returns hash with by_event_type breakdown
        if (!data.total_events && data.total_events !== 0) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderNutritionSummary(data)
        }
        break
      }
      default: {
        const items = data.data || []
        if (!items.length) {
          html = '<p class="text-xs text-slate-500 uppercase">NO_DATA_FOUND</p>'
        } else {
          html = this.renderGenericTable(items)
        }
      }
    }

    this.drilldownContentTarget.innerHTML = html
  }

  renderUsersTable(users) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">USER_ID</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">SESSIONS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">EVENTS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">LAST_ACTIVE</th>
          </tr>
        </thead>
        <tbody>
          ${users.map((u) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm font-mono">${this.safe(u.user_id)}</td>
              <td class="py-2 text-sm">${this.formatNumber(u.session_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(u.event_count || 0)}</td>
              <td class="py-2 text-xs text-slate-500">${this.safe(u.last_active)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderTimeTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">TIME</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">EVENTS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">USERS</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((t) => `
            <tr class="border-b border-slate-200 cursor-pointer hover:bg-slate-50"
                data-action="click->admin--analytics-dashboard#drillTimeDeeper"
                data-time="${this.safe(t.time_bucket || t.date, '')}">
              <td class="py-2 text-sm font-mono">${this.safe(t.time_bucket || t.date)}</td>
              <td class="py-2 text-sm">${this.formatNumber(t.event_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(t.unique_users || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderScreenTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">SCREEN</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">VIEWS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">USERS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">AVG_TIME</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((s) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm">${this.safe(s.screen_name)}</td>
              <td class="py-2 text-sm">${this.formatNumber(s.view_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(s.unique_users || 0)}</td>
              <td class="py-2 text-xs text-slate-500">${s.avg_time_on_screen ? `${this.escapeHtml(String(s.avg_time_on_screen))}s` : '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderErrorTable(items) {
    // FR-023: Render per-event error details with full context
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">ERROR</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">MESSAGE</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">OCCURRED</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">USER</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((e) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm text-red-700 truncate max-w-xs"
                  title="${this.safe(e.error || e.error_type || '-')}">
                ${this.safe(e.error || e.error_type || '-')}
              </td>
              <td class="py-2 text-sm truncate max-w-xs"
                  title="${this.safe(e.error_message || '-')}">
                ${this.safe(e.error_message || '-')}
              </td>
              <td class="py-2 text-xs text-slate-500">${this.safe(e.occurred_at || e.last_occurrence || '-')}</td>
              <td class="py-2 text-xs text-slate-500">${this.safe(e.user_id || '-')}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ${items.some((e) => e.stack_trace) ? `
        <details class="mt-4">
          <summary class="text-xs uppercase tracking-wide text-slate-600 cursor-pointer hover:text-slate-900">STACK_TRACES</summary>
          <div class="mt-2 space-y-2">
            ${items.filter((e) => e.stack_trace).map((e) => `
              <div class="p-2 bg-slate-50 border border-slate-200 rounded text-xs font-mono overflow-x-auto">
                <div class="text-red-700 mb-1">${this.safe(e.error || e.error_type || 'Error')}</div>
                <pre class="text-slate-600 whitespace-pre-wrap">${this.safe(e.stack_trace)}</pre>
              </div>
            `).join('')}
          </div>
        </details>
      ` : ''}
    `
  }

  renderGroupTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">GROUP</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">MEMBERS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">ACTIVITY</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((g) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm">${this.safe(g.group_name || g.group_id)}</td>
              <td class="py-2 text-sm">${this.formatNumber(g.member_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(g.activity_count || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderDeviceTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">DEVICE</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">OS_VERSION</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">USERS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">SESSIONS</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((d) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm">${this.safe(d.device_model)}</td>
              <td class="py-2 text-sm">${this.safe(d.os_version)}</td>
              <td class="py-2 text-sm">${this.formatNumber(d.user_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(d.session_count || 0)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderSyncTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">TYPE</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">SUCCESS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">FAILED</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">RATE</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((s) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm">${this.safe(s.sync_type)}</td>
              <td class="py-2 text-sm text-green-700">${this.formatNumber(s.success_count || 0)}</td>
              <td class="py-2 text-sm text-red-700">${this.formatNumber(s.failure_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(s.success_rate || 0)}%</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderWorkoutTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">TYPE</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">COUNT</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">AVG_DURATION</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">AVG_CALORIES</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((w) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm">${this.safe(w.workout_type)}</td>
              <td class="py-2 text-sm">${this.formatNumber(w.count || 0)}</td>
              <td class="py-2 text-sm">${w.avg_duration ? `${this.formatNumber(Math.round(w.avg_duration))}min` : '-'}</td>
              <td class="py-2 text-sm">${w.avg_calories ? `${this.formatNumber(Math.round(w.avg_calories))}kcal` : '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderNutritionTable(items) {
    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            <th class="text-left text-xs uppercase tracking-wide py-2">DATE</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">LOGS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">USERS</th>
            <th class="text-left text-xs uppercase tracking-wide py-2">AVG_CALS</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((n) => `
            <tr class="border-b border-slate-200">
              <td class="py-2 text-sm font-mono">${this.safe(n.date)}</td>
              <td class="py-2 text-sm">${this.formatNumber(n.log_count || 0)}</td>
              <td class="py-2 text-sm">${this.formatNumber(n.unique_users || 0)}</td>
              <td class="py-2 text-sm">${this.safe(n.avg_calories)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  renderGenericTable(items) {
    if (!items.length) return '<p class="text-xs text-slate-500 uppercase">NO_DATA</p>'

    const keys = Object.keys(items[0])

    return `
      <table class="w-full">
        <thead>
          <tr class="border-b-2 border-neutral-900">
            ${keys.map((k) => `<th class="text-left text-xs uppercase tracking-wide py-2">${this.escapeHtml(k)}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${items.map((item) => `
            <tr class="border-b border-slate-200">
              ${keys.map((k) => `<td class="py-2 text-sm">${this.safe(item[k])}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
  }

  // Summary renderers for hash-based API responses
  renderScreenSummary(data) {
    return `
      <div class="space-y-4">
        <div class="grid grid-cols-3 gap-4">
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">TOTAL_VIEWS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.total_views || 0)}</p>
          </div>
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">UNIQUE_VIEWERS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.unique_viewers || 0)}</p>
          </div>
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">AVG_DAILY</p>
            <p class="text-2xl font-bold">${Math.round(data.avg_daily_views || 0)}</p>
          </div>
        </div>
        ${data.device_breakdown ? this.renderBreakdownTable('DEVICE', data.device_breakdown.device_type || {}) : ''}
      </div>
    `
  }

  renderDeviceSummary(data) {
    return `
      <div class="space-y-4">
        <div class="text-center p-3 border-2 border-neutral-900">
          <p class="text-xs text-slate-500 uppercase">TOTAL_EVENTS</p>
          <p class="text-2xl font-bold">${this.formatNumber(data.total_events || 0)}</p>
        </div>
        ${this.renderBreakdownTable('DEVICE_TYPE', data.device_type || {})}
        ${this.renderBreakdownTable('OS_VERSION', data.os_version || {})}
        ${this.renderBreakdownTable('APP_VERSION', data.app_version || {})}
      </div>
    `
  }

  renderSyncSummary(data) {
    const byStatus = data.by_status || {}
    return `
      <div class="space-y-4">
        <div class="text-center p-3 border-2 border-neutral-900">
          <p class="text-xs text-slate-500 uppercase">${this.safe(data.sync_type)}_SYNC</p>
        </div>
        ${this.renderBreakdownTable('STATUS', byStatus)}
        ${data.error_breakdown ? this.renderBreakdownTable('ERRORS', data.error_breakdown) : ''}
      </div>
    `
  }

  renderWorkoutSummary(data) {
    return `
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">TOTAL_WORKOUTS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.total_workouts || 0)}</p>
          </div>
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">UNIQUE_USERS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.unique_users || 0)}</p>
          </div>
        </div>
        ${this.renderBreakdownTable('WORKOUT_TYPE', data.by_type || {})}
      </div>
    `
  }

  renderNutritionSummary(data) {
    return `
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">TOTAL_EVENTS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.total_events || 0)}</p>
          </div>
          <div class="text-center p-3 border-2 border-neutral-900">
            <p class="text-xs text-slate-500 uppercase">UNIQUE_USERS</p>
            <p class="text-2xl font-bold">${this.formatNumber(data.unique_users || 0)}</p>
          </div>
        </div>
        ${this.renderBreakdownTable('EVENT_TYPE', data.by_event_type || {})}
      </div>
    `
  }

  renderBreakdownTable(title, data) {
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1])
    if (!entries.length) return ''

    return `
      <div>
        <p class="text-xs text-slate-500 uppercase tracking-wide mb-2">${this.escapeHtml(title)}</p>
        <table class="w-full">
          <tbody>
            ${entries.map(([key, val]) => `
              <tr class="border-b border-slate-200">
                <td class="py-1 text-sm">${this.escapeHtml(key)}</td>
                <td class="py-1 text-sm text-right font-mono">${this.formatNumber(val)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `
  }

  renderDrilldownError(message) {
    if (this.hasDrilldownContentTarget) {
      this.drilldownContentTarget.innerHTML = `
        <div class="text-center py-8">
          <p class="text-xs text-red-600 uppercase tracking-wide">${this.escapeHtml(message)}</p>
        </div>
      `
    }
  }

  updateDrilldownBreadcrumbs() {
    if (!this.hasDrilldownBreadcrumbTrailTarget || !this.hasDrilldownTitleTarget) return

    const breadcrumbs = this.drilldownState.breadcrumbs
    const current = breadcrumbs[breadcrumbs.length - 1]

    // Update title
    this.drilldownTitleTarget.textContent = `[${current.label}]`

    // Build breadcrumb trail (excluding current)
    if (breadcrumbs.length > 1) {
      this.drilldownBreadcrumbTrailTarget.innerHTML = breadcrumbs.slice(0, -1).map((bc, i) => `
        <svg class="w-3 h-3 text-slate-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
        <a href="#"
           data-action="click->admin--analytics-dashboard#navigateDrilldown"
           data-breadcrumb-index="${i}"
           class="text-slate-500 hover:text-slate-900 uppercase tracking-wide">
          ${this.escapeHtml(bc.label)}
        </a>
      `).join('')

      // Add final separator and current label
      this.drilldownBreadcrumbTrailTarget.innerHTML += `
        <svg class="w-3 h-3 text-slate-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
        <span class="text-slate-900 uppercase tracking-wide">${this.escapeHtml(current.label)}</span>
      `
    } else {
      this.drilldownBreadcrumbTrailTarget.innerHTML = `
        <svg class="w-3 h-3 text-slate-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
        </svg>
        <span class="text-slate-900 uppercase tracking-wide">${this.escapeHtml(current.label)}</span>
      `
    }
  }

  updateDrilldownPagination() {
    if (!this.hasDrilldownPaginationTarget) return

    const { page, totalPages, totalResults } = this.drilldownState

    if (totalResults > 0) {
      this.drilldownPaginationTarget.classList.remove('hidden')

      if (this.hasDrilldownResultCountTarget) {
        this.drilldownResultCountTarget.textContent = this.formatNumber(totalResults)
      }
      if (this.hasDrilldownCurrentPageTarget) {
        this.drilldownCurrentPageTarget.textContent = page
      }
      if (this.hasDrilldownTotalPagesTarget) {
        this.drilldownTotalPagesTarget.textContent = totalPages
      }
      if (this.hasDrilldownPrevBtnTarget) {
        this.drilldownPrevBtnTarget.disabled = page <= 1
      }
      if (this.hasDrilldownNextBtnTarget) {
        this.drilldownNextBtnTarget.disabled = page >= totalPages
      }
    } else {
      this.drilldownPaginationTarget.classList.add('hidden')
    }
  }

  updateDrilldownExportLink() {
    if (!this.hasDrilldownExportLinkTarget || !this.hasDrilldownExportUrlValue) return

    const params = new URLSearchParams({
      type: this.drilldownState.type,
      ...this.drilldownState.params
    })

    if (this.hasStartDateValue) params.set('start_date', this.startDateValue)
    if (this.hasEndDateValue) params.set('end_date', this.endDateValue)

    this.drilldownExportLinkTarget.href = `${this.drilldownExportUrlValue}?${params}`
  }

  drilldownPrevPage() {
    if (this.drilldownState.page > 1) {
      this.drilldownState.page -= 1
      this.loadDrilldownData()
    }
  }

  drilldownNextPage() {
    if (this.drilldownState.page < this.drilldownState.totalPages) {
      this.drilldownState.page += 1
      this.loadDrilldownData()
    }
  }

  showDrilldownModal() {
    if (this.hasDrilldownModalTarget) {
      this.drilldownModalTarget.classList.remove('hidden')
      document.body.classList.add('overflow-hidden')

      // Focus trap - focus first focusable element
      const firstFocusable = this.drilldownModalTarget.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) firstFocusable.focus()

      // Add escape key listener
      this.escapeHandler = (e) => {
        if (e.key === 'Escape') this.closeDrilldown()
      }
      document.addEventListener('keydown', this.escapeHandler)
    }
  }

  closeDrilldown() {
    if (this.hasDrilldownModalTarget) {
      this.drilldownModalTarget.classList.add('hidden')
      document.body.classList.remove('overflow-hidden')

      // Remove escape key listener
      if (this.escapeHandler) {
        document.removeEventListener('keydown', this.escapeHandler)
        this.escapeHandler = null
      }
    }
  }

  showDrilldownLoading() {
    if (this.hasDrilldownLoadingTarget) {
      this.drilldownLoadingTarget.classList.remove('hidden')
    }
  }

  hideDrilldownLoading() {
    if (this.hasDrilldownLoadingTarget) {
      this.drilldownLoadingTarget.classList.add('hidden')
    }
  }

  drillTimeDeeper(event) {
    const timeValue = event.currentTarget.dataset.time
    if (!timeValue) return

    // Determine next granularity
    const currentType = this.drilldownState.type
    const nextParams = { ...this.drilldownState.params }

    if (currentType === 'time') {
      const granularity = this.drilldownState.params.granularity || 'day'

      if (granularity === 'day') {
        nextParams.granularity = 'hour'
        nextParams.date = timeValue
        this.drillInto('time', nextParams, `HOUR_${timeValue}`)
      } else if (granularity === 'hour') {
        nextParams.granularity = 'minute'
        nextParams.hour = timeValue
        this.drillInto('time', nextParams, `MIN_${timeValue}`)
      }
    }
  }

  // Toast notification methods
  showToast(message, type = 'info', duration = 5000) {
    if (!this.hasToastContainerTarget) return

    const toastId = `toast-${Date.now()}`
    const colorClasses = {
      error: 'bg-red-100 border-red-500 text-red-800',
      warning: 'bg-yellow-100 border-yellow-500 text-yellow-800',
      success: 'bg-green-100 border-green-500 text-green-800',
      info: 'bg-blue-100 border-blue-500 text-blue-800'
    }

    const iconPaths = {
      error: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    }

    const toastColorClass = colorClasses[type] || colorClasses.info
    const toastHtml = `
      <div id="${toastId}"
           class="flex items-center gap-3 px-4 py-3 border-2 border-neutral-900
                  shadow-[4px_4px_0_#171717] ${toastColorClass} animate-slide-in-right"
           role="alert">
        <svg class="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPaths[type] || iconPaths.info}"></path>
        </svg>
        <span class="text-xs uppercase tracking-wide flex-1">${this.escapeHtml(message)}</span>
        <button type="button"
                data-action="click->admin--analytics-dashboard#dismissToast"
                data-toast-id="${toastId}"
                class="text-current opacity-70 hover:opacity-100">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `

    this.toastContainerTarget.insertAdjacentHTML('beforeend', toastHtml)

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        this.dismissToastById(toastId)
      }, duration)
    }
  }

  dismissToast(event) {
    const toastId = event.currentTarget.dataset.toastId
    this.dismissToastById(toastId)
  }

  dismissToastById(toastId) {
    const toast = document.getElementById(toastId)
    if (toast) {
      toast.classList.add('animate-slide-out-right')
      setTimeout(() => toast.remove(), 200)
    }
  }

  // WebSocket Connection Monitoring Methods
  async initializeWebSocket() {
    try {
      // Dynamic import of consumer
      const { default: consumer } = await import('~/channels/consumer')
      this.consumer = consumer

      this.subscription = this.consumer.subscriptions.create(
        {
          channel: 'Admin::AnalyticsDashboardChannel',
          dashboard: this.dashboardTypeValue
        },
        {
          connected: () => this.handleConnected(),
          disconnected: () => this.handleDisconnected(),
          rejected: () => this.handleRejected(),
          received: (data) => this.handleReceived(data)
        }
      )
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error)
      this.updateConnectionStatus('error', 'WebSocket initialization failed')
    }
  }

  disconnectWebSocket() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleConnected() {
    this.connectionState.status = 'connected'
    this.connectionState.connectedAt = new Date()
    this.connectionState.reconnectAttempts = 0

    this.updateConnectionStatus('connected', 'Connected')
    this.startHeartbeat()

    // Request initial status
    if (this.subscription) {
      this.subscription.perform('status')
    }
  }

  handleDisconnected() {
    this.connectionState.status = 'disconnected'
    this.connectionState.connectionId = null

    this.updateConnectionStatus('disconnected', 'Disconnected')
    this.stopHeartbeat()

    // Attempt reconnection after delay
    this.scheduleReconnect()
  }

  handleRejected() {
    this.connectionState.status = 'rejected'
    this.updateConnectionStatus('error', 'Connection rejected (unauthorized)')
    this.showToast('WebSocket connection rejected. Please refresh the page.', 'error')
  }

  handleReceived(data) {
    switch (data.event) {
      case 'connection_established':
        this.connectionState.connectionId = data.connection_id
        this.updateConnectionStatus('connected', `Connected (${data.connection_id.slice(0, 8)}...)`)
        break

      case 'pong':
        this.connectionState.lastPong = new Date()
        this.connectionState.latencyMs = data.latency_ms
        this.updateConnectionLatency(data.latency_ms, data.uptime_seconds)
        break

      case 'connection_status':
        this.connectionState.connectionId = data.connection_id
        break

      case 'metrics_update':
      case 'initial_metrics':
        this.handleMetricsUpdate(data)
        break

      case 'alert':
        this.handleAlert(data.alert)
        break

      default:
        console.log('Unknown WebSocket event:', data.event)
    }
  }

  handleMetricsUpdate(data) {
    if (data.dashboard === this.dashboardTypeValue && data.metrics) {
      this.updateDashboard(data.metrics)
      this.showToast('Dashboard updated', 'info', 2000)
    }
  }

  handleAlert(alert) {
    const severity = alert.severity === 'critical' ? 'error' : 'warning'
    this.showToast(alert.message, severity, 10000)
  }

  startHeartbeat() {
    this.stopHeartbeat()

    // Send heartbeat every 30 seconds
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat()
    }, 30000)

    // Send initial heartbeat
    this.sendHeartbeat()
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  sendHeartbeat() {
    if (this.subscription && this.connectionState.status === 'connected') {
      this.subscription.perform('heartbeat', {
        timestamp: new Date().toISOString()
      })
    }
  }

  scheduleReconnect() {
    const maxAttempts = 5
    const baseDelay = 1000

    if (this.connectionState.reconnectAttempts < maxAttempts) {
      this.connectionState.reconnectAttempts += 1
      const delay = baseDelay * 2 ** (this.connectionState.reconnectAttempts - 1)

      this.updateConnectionStatus('reconnecting', `Reconnecting in ${delay / 1000}s...`)

      setTimeout(() => {
        if (this.connectionState.status !== 'connected') {
          this.initializeWebSocket()
        }
      }, delay)
    } else {
      this.updateConnectionStatus('error', 'Max reconnection attempts reached')
      this.showToast('Connection lost. Please refresh the page.', 'error', 0)
    }
  }

  updateConnectionStatus(status, message) {
    this.connectionState.status = status

    if (this.hasConnectionStatusTarget) {
      this.connectionStatusTarget.textContent = message
    }

    if (this.hasConnectionIndicatorTarget) {
      const indicator = this.connectionIndicatorTarget
      indicator.classList.remove('bg-green-500', 'bg-red-500', 'bg-yellow-500', 'bg-slate-400')

      switch (status) {
        case 'connected':
          indicator.classList.add('bg-green-500')
          break
        case 'disconnected':
        case 'error':
          indicator.classList.add('bg-red-500')
          break
        case 'reconnecting':
          indicator.classList.add('bg-yellow-500')
          break
        default:
          indicator.classList.add('bg-slate-400')
      }
    }
  }

  updateConnectionLatency(latencyMs, uptimeSeconds) {
    if (this.hasConnectionStatusTarget && latencyMs !== null) {
      const uptime = this.formatUptime(uptimeSeconds)
      this.connectionStatusTarget.textContent = `Connected (${latencyMs}ms, ${uptime})`
    }
  }

  formatUptime(seconds) {
    if (!seconds || seconds < 60) return `${seconds || 0}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  // Manual refresh via WebSocket
  requestRefresh() {
    if (this.subscription && this.connectionState.status === 'connected') {
      this.subscription.perform('refresh')
    } else {
      this.loadData()
    }
  }
}
